import React, { useEffect, useMemo, useRef, useState, useCallback, useContext, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { downloadModel, deleteModel, reprocessModel, processModelByFormat } from 'store/actions/rpc';
import {
  setSelectedUserTeam,
  resetCreators,
  resetUserModels,
  resetUserPurchasedModels,
  resetTeamModels,
  resetUserJobs,
} from 'store/actions/userData';
import {
  VerticalSpacer,
  H3,
  H5,
  Body,
  FovLoader,
  DocNote,
  FovDrawer,
} from 'components';
import { decode, wssCallback } from 'utils';
import { ModelCard } from './ModelCard';
import { listColumns } from './listColumns';
import { NoSelectionMessage } from 'components/filterPanel/panelStyles';
import { paramsFromURL, paramsToObject } from 'utils/paramHelpers';
import { withTheme } from 'styled-components';
import { clearMessages } from 'store/actions/app';
import constants from 'constants/index';
import _ from 'lodash';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { ModelContext } from '../ModelContext';
import { getScaledValues } from './utils';
import { JOB_STATUSES } from 'utils/enums';
import { hasAccess } from 'navigation/navigationHelpers';
import { superUsers } from 'components/generic/ProtectedContent';

import {
  TableFullWidth,
  ModelsArea,
  PreviewTitle,
  WelcomeLink,
  SkeletonCardsGrid,
  SkeletonCard,
  SkeletonCardSide,
  SkeletonCardThumb,
  SkeletonCardTitle,
  SkeletonBlock,
  SkeletonTable,
  SkeletonTableRow,
} from './styles';

const { CONTEXT_VALUES } = constants;
const VIEWS = { CARD: 0, TABLE: 1 };
const { FAILED } = JOB_STATUSES;

const Models = withTheme(props => {
  const { theme } = props;
  const [ selectedModel, setSelectedModel ] = useState(null);
  const [ previewURL, setPreviewURL ] = useState(null);
  const [ cookies ] = useCookies([ 'userAuth' ]);
  const {
    getModels,
    workingData,
    getFreeModels,
    onBeforeDelete,
  } = useContext(ModelContext);
  const location = useLocation();

  const [ items, setItems ] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = cookies.archvision_token;
  const userDataStore = useSelector(state => state.userData);
  const temporaryRole = userDataStore?.temporaryRole;
  const selectedTheme = userDataStore.userPreferences.theme;
  const modelsLoading = userDataStore.modelsLoading;
  const userPreferences = userDataStore.userPreferences;
  const activeView = userPreferences?.modelView;
  const cardScale = userPreferences?.cardScaling;
  const onCreators = location.pathname.includes('/creators');
  const onMyFiles = location.pathname.includes('/my-files');
  const onJobs = location.pathname.includes('/jobs');
  const onTeams = location.pathname.includes('/team-models');
  const onPurchasedModels = location.pathname.includes('/purchased-models');
  const creators = useMemo(() => userDataStore.creators, [ userDataStore.creators ]);
  const freeModels = useMemo(() => userDataStore.freeModels, [ userDataStore.freeModels ]);
  const userModels = useMemo(() => userDataStore.userModels, [ userDataStore.userModels ]);
  const userJobs = useMemo(() => userDataStore.userJobs, [ userDataStore.userJobs ]);
  const purchasedModels = useMemo(() => userDataStore.purchasedModels, [ userDataStore.purchasedModels ]);
  const teamModels = useMemo(() => userDataStore.teamModels, [ userDataStore.teamModels ]);
  const selectedTeamId = useMemo(() => userDataStore.selectedTeamId, [ userDataStore.selectedTeamId ]);
  const [ searchParams, setSearchParams ] = useSearchParams();
  const params = paramsToObject(decode(paramsFromURL()));
  const [ previewId, setPreviewId ] = useState(location.state?.previewId ?? null);

  const userRoles =
    temporaryRole && temporaryRole !== 'default'
    ? [ temporaryRole ] : userDataStore?.data?.roles ?? [];

  const isAllowed = hasAccess(userRoles, superUsers);

  useEffect(() => {
    if (!onCreators) {
      dispatch(resetCreators());
    }
    if (!onMyFiles) {
      dispatch(resetUserModels());
    }
    if (!onJobs) {
      dispatch(resetUserJobs());
    }
    if (!onPurchasedModels) {
      dispatch(resetUserPurchasedModels());
    }
    if (!onTeams) {
      dispatch(resetTeamModels());
    }
  }, [ location.pathname ]);

  useEffect(() => {
    if (onCreators && freeModels === undefined) {
      getFreeModels();
    }
  }, [ freeModels ]);

  useEffect(() => {
    if (onTeams && params.selectedTeam && params.selectedTeam !== selectedTeamId) {
      dispatch(setSelectedUserTeam(params.selectedTeam || selectedTeamId));
      getModels(params.selectedTeam || selectedTeamId);
    }
  }, [ params, selectedTeamId ]);


  const [ models, totalCount, currentPage, pageSize, isNextPage ] = useMemo(() => {
    const firstJobGuid = workingData?.filteredResults?.[0]?.models?.[0]?.job_guid;
    const shouldUseDirectModels = firstJobGuid && firstJobGuid !== '';

    const models = shouldUseDirectModels
      ? workingData?.filteredResults?.[0]?.models ?? []
      : workingData?.filteredResults?.flatMap(partner => partner.models) ?? [];

    return [
      models,
      workingData?.filteredTotalModels,
      workingData?.currentPage,
      workingData?.pageSize,
      workingData?.isNextPage,
    ];
  }, [ onCreators, creators, userModels, userJobs, purchasedModels, teamModels ]);

  const [ loadMore, setLoadMoe ] = useState(false);
  const observerRef = useRef();
  const [ loading, setLoading ] = useState(false);

  const lastModelElementRef = useCallback(node => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      const [ target ] = entries;

      if (!isNextPage && previewId) {
        const modelIndex = items.map(item => item.rpc_guid).indexOf(previewId);
        if (modelIndex !== -1) {
          onModelClick(items[modelIndex]);
          setPreviewId(null);
        }
      }

      if (target.isIntersecting && isNextPage && !loading) {
        const page = Number(searchParams.get('page'));

        if (items.length === page * pageSize) {
          setLoadMoe(true);
          setLoading(true);
          searchParams.set('page', page + 1);
          setSearchParams(searchParams, { replace: true });
          getModels();
        }
      }
    });

    if (node) observerRef.current.observe(node);
  }, [ items, previewId ]);

  useEffect(() => {
    if (!searchParams.has('page')) searchParams.set('page', 1);
    if (!searchParams.has('per_page')) searchParams.set('per_page', 50);

    const page = Number(searchParams.get('page'));

    if (models.length === 0) {
      return;
    }

    if (items.length < (page - 1) * pageSize && page !== 1) {
      searchParams.set('page', 1);
      setSearchParams(searchParams, { replace: true });
      setItems([]);
      getModels();
      return;
    }

    const haveSameGuids = (arr1, arr2) => {
      const toKey = item => `${item.rpc_guid ?? item.job_guid}-${item.status}`;
      const set1 = new Set(arr1.map(toKey));
      const set2 = new Set(arr2.map(toKey));

      if (set1.size !== set2.size) return false;

      for (const guid of set1) {
        if (!set2.has(guid)) return false;
      }

      return true;
    };

    if (loadMore) {
      setItems(prevItems => [ ...prevItems, ...models ]);
      setLoadMoe(false);
      setLoading(false);
    } else if (items.length === 0) {
      setItems(models);
    } else if (page === 1) {
      if (!haveSameGuids(items, models)) {
        setItems(models);
      }
    }
  }, [ models ]);

  const onModelClick = model => {
    const { rpc_guid } = model;
    const selectedGuid = selectedModel?.rpc_guid;

    if (rpc_guid) {
      if (!selectedModel || rpc_guid !== selectedGuid) {
        if (onCreators) {
          navigate(`/preview-model?rpc_guid=${rpc_guid}&context=${CONTEXT_VALUES.CREATORS}`);
        } else if (onMyFiles) {
          navigate(`/preview?rpc_guid=${rpc_guid}&context=${CONTEXT_VALUES.USER}`);
        } else if (onPurchasedModels) {
          navigate(`/preview?rpc_guid=${rpc_guid}&context=${CONTEXT_VALUES.PURCHASED}`);
        } else if (onTeams) {
          navigate(`/preview?rpc_guid=${rpc_guid}&context=${CONTEXT_VALUES.TEAM}`);
        }
      }
    } else if (model.job_guid) {
      if (!selectedModel || model.job_guid !== selectedModel.job_guid) {
        setSelectedModel(model);
        return;
      }

      previewCleanUp();
    }
  };

  const isDefaultParams = () => {
    return (
      (!searchParams.has('search') || searchParams.get('search') === '') &&
      (!searchParams.has('tags') || searchParams.get('tags') === '') &&
      (!searchParams.has('attributes') || searchParams.get('attributes') === '') &&
      (!searchParams.has('search_subcategory') || searchParams.get('search_subcategory') === '') &&
      (!searchParams.has('search_category') || searchParams.get('search_category') === '')
    );
  };

  const handleOnDownload = useCallback((rpcGuid, format, onComplete) => {
    const args = { rpcGuid, format, token, download: true, onComplete };
    dispatch(downloadModel(args));
  }, []);

  const handleOnPublish = useCallback((rpcGuid, format, callbacks) => {
    const { onError } = callbacks ?? {};
    dispatch(processModelByFormat({ rpcGuid, format, token, onError }));
  }, []);

  const handleOnDelete = useCallback(model => {
    const ignoreLoading = true;
    onBeforeDelete?.(model);
    dispatch(deleteModel(model, token)).then(() => getModels({ ignoreLoading }));
  }, []);

  const handleOnReprocess = useCallback(model => {
    const ignoreLoading = true;
    const wssArgs = { token, dispatch, userModels };

    const reprocessArgs = {
      job: model,
      token,
      handleSockets: () => wssCallback(wssArgs),
    };

    dispatch(reprocessModel(reprocessArgs)).then(() => getModels({ ignoreLoading }));
  }, []);

  const previewCleanUp = resetModel => {
    setPreviewURL(null);
    if (!resetModel) setSelectedModel(null);
    dispatch(clearMessages());
  };

  const handlePageChange = page => {
    if (Number(searchParams.get('page')) === page) return;
  };

  const handleWelcomeClick = () => {
    navigate('/welcome', { state: { noModels: true } });
  };

  const showModelsSkeleton = _.isEmpty(models) && _.isEmpty(items) && _.isEmpty(workingData?.filteredResults);

  const buildModelsSkeleton = () => {
    const scaling = getScaledValues(cardScale || 100);

    if (activeView === VIEWS.TABLE) {
      return (
        <SkeletonTable>
          {Array.from({ length: 8 }).map((_, index) => (
            <SkeletonTableRow
              key={`models-table-skeleton-${index}`}
              theme={theme}
              $selectedTheme={selectedTheme}
            >
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={96}
                $height={84}
              />
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width='60%'
                $height={20}
              />
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={40}
                $height={40}
                $radius='999rem'
              />
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={120}
                $height={20}
              />
            </SkeletonTableRow>
          ))}
        </SkeletonTable>
      );
    }

    return (
      <SkeletonCardsGrid>
        {Array.from({ length: 24 }).map((_, index) => (
          <SkeletonCard
            key={`models-card-skeleton-${index}`}
            theme={theme}
            $selectedTheme={selectedTheme}
            column={scaling.grid.column}
            row={scaling.grid.row}
            width={scaling.cardContainer.width}
            height={scaling.cardContainer.height}
          >
            <SkeletonCardSide $area='icon'>
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={scaling.jobStatus.width}
                $height={scaling.jobStatus.height}
                $radius='999rem'
              />
            </SkeletonCardSide>

            <SkeletonCardThumb>
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={scaling.image.width}
                $height={scaling.image.height}
              />
            </SkeletonCardThumb>

            <SkeletonCardSide $area='actions'>
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={scaling.icons.width}
                $height={scaling.icons.height}
                $radius='999rem'
              />
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={scaling.icons.width}
                $height={scaling.icons.height}
                $radius='999rem'
              />
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width={scaling.icons.width}
                $height={scaling.icons.height}
                $radius='999rem'
              />
            </SkeletonCardSide>

            <SkeletonCardTitle>
              <SkeletonBlock
                theme={theme}
                $selectedTheme={selectedTheme}
                $width='72%'
                $height={18}
              />
            </SkeletonCardTitle>
          </SkeletonCard>
        ))}
      </SkeletonCardsGrid>
    );
  };

  const buildModels = () => {
    const isCardView = activeView === VIEWS.CARD;
    const shouldRender = !_.isEmpty(models);

    if (shouldRender) {
      const filteredModels = items?.filter(
        model => !(isDefaultParams() && onCreators && model.free_download === 1),
      ) ?? [];

      const visibleModels = (isDefaultParams() && onCreators)
        ? [ ...(freeModels || []), ...filteredModels ]
        : filteredModels;

      const modelsAsList = (
        <TableFullWidth
          dataSource={visibleModels}
          columns={listColumns(isCardView, handleOnDownload, handleOnDelete, selectedTheme, handleOnPublish)}
          rowKey={`row-${Math.random()}`}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onCellClick={model => onModelClick(model)}
          lastModelElementRef={lastModelElementRef}
          selectedGuid={onJobs ? selectedModel?.job_guid : selectedModel?.rpc_guid}
          selectedKey={onJobs ? 'job_guid' : 'rpc_guid'}
        />
      );

      const modelsAsCards = visibleModels?.map((model, index) => {
        const { rpc_guid, job_guid } = model;
        const guid = job_guid || rpc_guid;
        const selectedGuid = selectedModel?.job_guid || selectedModel?.rpc_guid;

        const modelCardInfinite = (
          <ModelCard
            key={index}
            job={model}
            onDownload={handleOnDownload}
            onPublish={handleOnPublish}
            onDelete={() => handleOnDelete(model)}
            onReprocess={() => handleOnReprocess(model)}
            cardScale={cardScale}
            onCardClick={() => onModelClick(model)}
            isCardView={isCardView}
            cardSelected={selectedModel && guid === selectedGuid}
          />
        );

        return index === items.length - 1
          ? <div ref={lastModelElementRef} key={index}>{modelCardInfinite}</div>
          : modelCardInfinite;
      });

      return isCardView ? modelsAsCards : modelsAsList;
    }

    if (onMyFiles && isDefaultParams() && _.isEmpty(models)) {
      return (
        <NoSelectionMessage theme={theme} $selectedTheme={selectedTheme}>
          <VerticalSpacer size={25} />
          <H3>Create your first model.</H3>
          <p>
            <>
              Check out the free models on the
              <WelcomeLink
                theme={theme}
                $selectedTheme={selectedTheme}
                onClick={handleWelcomeClick}
              >
                Welcome
              </WelcomeLink>
              page.
            </>
          </p>
        </NoSelectionMessage>
      );
    }

    return (
      <NoSelectionMessage theme={theme} $selectedTheme={selectedTheme}>
        <VerticalSpacer size={25} />

        <H3>
          {_.isEmpty(params.creators) && onCreators && 'No creators selected'}
          {_.isEmpty(models) && !onJobs && (
            `${_.isEmpty(params.creators) && onCreators ? ' and n' : 'N'}o matching content found.`
          )}
        </H3>

         <H3>
          {_.isEmpty(models) && onJobs && 'You have no active jobs.'}
        </H3>
          {_.isEmpty(models) && onJobs && (
            <p>
              Jobs will appear here when a model is being processed.
            </p>
          )}

        {_.isEmpty(models) && params.search && !onJobs && (
          <p>
            No results returned for &quot;{decode(params.search)}&quot;.
          </p>
        )}

        <p>
          {_.isEmpty(params.creators) && onCreators && 'Please select a creator to view their content'}
          {_.isEmpty(models) && !onJobs && (
            `${_.isEmpty(params.creators) && onCreators
              ? ' and t' : 'T'}ry refining your ${params.search ? 'search' : 'filters'}.`
          )}
        </p>
      </NoSelectionMessage>
    );
  };

  return (
    <>
      <ModelsArea>
        <DocNote path={[ 'content', 'models' ]} top={5} left={2} />

        {showModelsSkeleton ? (
          buildModelsSkeleton()
        ) : (
          <FovLoader text='Filtering results' resource={!modelsLoading}>
            {buildModels()}
          </FovLoader>
        )}
      </ModelsArea>

      <FovDrawer
        closeDrawer={() => previewCleanUp()}
        isOpen={!onJobs ? previewURL : selectedModel}
        title={!onJobs ? selectedModel?.title : selectedModel?.job_guid}
      >
        {selectedModel?.job_guid && (
          <Body>
            <Fragment>
              <PreviewTitle>
                <VerticalSpacer size={16} />
                <H5>File Name:</H5>
                <span>{selectedModel.filename}</span>

                <VerticalSpacer size={8} />
                <H5>Job GUID:</H5>
                <span>{selectedModel.job_guid}</span>

                <VerticalSpacer size={8} />
                <H5>Status:</H5>
                <span>{selectedModel.status}</span>

                <VerticalSpacer size={8} />
                <H5>Type:</H5>
                <span>{selectedModel.type}</span>

                <VerticalSpacer size={8} />
                <H5>Created At:</H5>
                <span>{new Date(selectedModel.created_at).toLocaleString()}</span>

                <VerticalSpacer size={8} />
                <H5>Application:</H5>
                <span>{selectedModel.application}</span>

                <VerticalSpacer size={8} />
                {isAllowed && selectedModel?.status === FAILED && (
                  <a
                    href={`https://cdn.archvision.services/public/failed-jobs/${selectedModel.job_guid}.zip`}
                    download>
                      <button type='button'>Download</button>
                    </a>
                )}
              </PreviewTitle>
            </Fragment>
          </Body>
        )}
      </FovDrawer>
    </>
  );
});

export { Models };